/**
 * OAuth Helper Utility
 * Reusable utility for handling OAuth popup windows
 */

interface OAuthPopupOptions {
  url: string;
  platform: string;
  width?: number;
  height?: number;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Opens an OAuth popup window and monitors its lifecycle
 * @param options Configuration for the OAuth popup
 * @returns Promise that resolves when popup closes
 */
export const openOAuthPopup = ({
  url,
  platform,
  width = 600,
  height = 700,
  onSuccess,
  onError,
}: OAuthPopupOptions): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      // Calculate center position for popup
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;

      // Window features for centered popup
      const windowFeatures = [
        `width=${width}`,
        `height=${height}`,
        `left=${left}`,
        `top=${top}`,
        'toolbar=no',
        'location=no',
        'status=no',
        'menubar=no',
        'scrollbars=yes',
        'resizable=yes',
      ].join(',');

      // Open popup window
      const popupWindow = window.open(url, `oauth_${platform}`, windowFeatures);

      if (!popupWindow) {
        const error = 'Popup blocked. Please allow popups for this site.';
        onError?.(error);
        reject(new Error(error));
        return;
      }

      // Focus the popup
      popupWindow.focus();

      // Monitor popup closure
      const checkInterval = setInterval(() => {
        if (popupWindow.closed) {
          clearInterval(checkInterval);

          // Notify parent window of integration completion
          window.postMessage(
            {
              type: 'INTEGRATION_ACTIVATED',
              platform,
              timestamp: Date.now(),
            },
            window.location.origin
          );

          onSuccess?.();
          resolve();
        }
      }, 500); // Check every 500ms

      // Safety cleanup after 10 minutes (OAuth should complete way before this)
      setTimeout(() => {
        if (!popupWindow.closed) {
          clearInterval(checkInterval);
          popupWindow.close();
          const error = 'OAuth flow timed out';
          onError?.(error);
          reject(new Error(error));
        }
      }, 600000); // 10 minutes
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to open OAuth popup';
      onError?.(errorMessage);
      reject(error);
    }
  });
};

/**
 * Listen for OAuth messages from child windows
 * @param callback Function to call when OAuth completes
 * @returns Cleanup function to remove listener
 */
export const listenForOAuthComplete = (
  callback: (platform: string) => void
): (() => void) => {
  const handleMessage = (event: MessageEvent) => {
    // Verify message origin for security
    if (event.origin !== window.location.origin) {
      return;
    }

    // Handle integration activation message
    if (event.data?.type === 'INTEGRATION_ACTIVATED') {
      callback(event.data.platform);
    }
  };

  window.addEventListener('message', handleMessage);

  // Return cleanup function
  return () => {
    window.removeEventListener('message', handleMessage);
  };
};

/**
 * Get integration status from list
 * @param integrations List of integrations
 * @param platform Platform name to check
 * @returns Integration object if found and active (state === 'OK')
 */
export const getIntegrationStatus = (
  integrations: any[],
  platform: string
): { isActive: boolean; integration: any | null } => {
  const integration = integrations.find(
    (int) => int.integration_type.toLowerCase() === platform.toLowerCase() &&
             int.state === 'OK'  // Check for 'OK' status from API
  );

  return {
    isActive: !!integration,
    integration: integration || null,
  };
};
